import { apiFetch } from '@carouselgg/auth/client'
import type { RotatingPass } from '@carouselgg/auth/pass-code'

// The player side of venue check-in: what a scanned code opens, checking in,
// and the ways to sign up at the door when someone isn't registered yet.

/**
 * awaiting_payment: on a team that hasn't paid its entry fee at the venue yet.
 * They pay at the desk, then check in.
 */
export type CheckInState = 'checked_in' | 'on_roster' | 'pending_team' | 'awaiting_payment' | 'free_agent' | 'not_registered'

export interface CheckInSeason {
  id: string
  name: string
  slug: string
  competition_type: string
  status: string
  starts_at: string | null
  venue_name: string
  venue_address: string
  org: { id: string; name: string; slug: string; logo_url: string }
  game: { name: string; team_size: number }
}

export interface CheckInLink {
  season: CheckInSeason
  open: boolean
  closed_reason: string
  /** Whether players may check themselves in here. Off: show your pass at the desk. */
  self_check_in: boolean
  /** Null when signed out. */
  me: { state: CheckInState; team?: { id: string; name: string } | null; checked_in_at?: string | null } | null
  /** Which door sign-up paths are open right now. */
  door: { register_team: boolean; join_team: boolean; free_agent: boolean }
}

export interface CheckInResult {
  checked_in_at: string
  team: { id: string; name: string } | null
  already_checked_in: boolean
}

export interface DoorTeam {
  team_id: string
  name: string
  logo_url: string
  members: number
  capacity: number
}

export function getCheckInLink(token: string): Promise<CheckInLink> {
  return apiFetch<CheckInLink>(`/check-in/t/${encodeURIComponent(token)}`)
}

export function checkIn(token: string): Promise<CheckInResult> {
  return apiFetch<CheckInResult>(`/check-in/t/${encodeURIComponent(token)}`, { method: 'POST' })
}

export async function listDoorTeams(token: string): Promise<DoorTeam[]> {
  const res = await apiFetch<{ teams: DoorTeam[] }>(`/check-in/t/${encodeURIComponent(token)}/teams`)
  return res.teams
}

/** Asks to join a registered team — the existing free-agent application, approved by its captain or the desk. */
export function applyToTeam(orgId: string, teamId: string, seasonId: string): Promise<unknown> {
  return apiFetch(`/recruit/${orgId}/teams/${teamId}/apply`, {
    method: 'POST',
    body: JSON.stringify({ season_id: seasonId }),
  })
}

export interface PassInfo {
  season: { id: string; name: string; org: { id: string; name: string; slug: string } }
  player: { user_id: string; display_name: string; username: string; avatar_url: string }
  team: { id: string; name: string } | null
  checked_in_at: string | null
  expired: boolean
  /** True when the viewer is staff for this tournament and can check the player in. */
  can_check_in: boolean
}

export interface ScanResult {
  player: { user_id: string; display_name: string; username: string; avatar_url: string; is_guest: boolean }
  team: { id: string; name: string } | null
  already_checked_in: boolean
  checked_in_at: string
}

export function getPass(payload: string): Promise<PassInfo> {
  return apiFetch<PassInfo>(`/check-in/pass/${encodeURIComponent(payload)}`)
}

export function scanPass(orgId: string, payload: string): Promise<ScanResult> {
  return apiFetch<ScanResult>(`/check-in/${orgId}/scan`, { method: 'POST', body: JSON.stringify({ payload }) })
}

export interface MyPass {
  season: {
    id: string
    name: string
    slug: string
    starts_at: string | null
    venue_name: string
    venue_address: string
    org: { id: string; name: string; slug: string; logo_url: string }
  }
  team: { id: string; name: string } | null
  checked_in_at: string | null
  /** Rotating: the page derives a fresh code every period from the seed. */
  pass: RotatingPass
}

/** Your passes for in-person tournaments with check-in open, and when they arrived (for clock correction). */
export async function getMyPasses(): Promise<{ passes: MyPass[]; fetchedAt: number }> {
  const fetchedAt = Date.now()
  const res = await apiFetch<{ competitions: MyPass[] }>('/check-in/mine')
  return { passes: res.competitions, fetchedAt }
}
